import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Trash2, Settings, AlertCircle, CheckCircle, Info, Clock, RefreshCw } from 'lucide-react';
import { AppNotification, NotificationType } from '../types/notification';
import notificationService from '../services/notificationService';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface NotificationCenterProps {
    className?: string;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ className = '' }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Load notifications
    const loadNotifications = useCallback(async (page: number = 1, append: boolean = false, force: boolean = false) => {
        if (loading && !force) return;
        
        setLoading(true);
        try {
            const response = await notificationService.getNotifications(page, 10);
            const newNotifications = response.data;
            
            
            if (append) {
                setNotifications(prev => [...prev, ...newNotifications]);
            } else {
                setNotifications(newNotifications);
            }
            
            setUnreadCount(response.unread);
            try { (window as any).__setFaviconUnread?.(response.unread); } catch {}
            setHasMore(newNotifications.length === 10);
            setCurrentPage(page);
        } catch (error) {
            console.error('Failed to load notifications:', error);
        } finally {
            setLoading(false);
        }
    }, []); // Remove loading dependency to prevent constant re-creation

    // Load more notifications
    const loadMore = () => {
        if (!loading && hasMore) {
            loadNotifications(currentPage + 1, true);
        }
    };

    // Mark notification as read
    const markAsRead = async (notificationId: string) => {
        try {
            await notificationService.markAsRead(notificationId);
            setNotifications(prev => 
                prev.map(notif => 
                    notif.id === notificationId ? { ...notif, read: true } : notif
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    // Mark all as read
    const markAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all notifications as read:', error);
        }
    };

    // Delete notification
    const deleteNotification = async (notificationId: string) => {
        try {
            await notificationService.deleteNotification(notificationId);
            setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
            // Update unread count if the deleted notification was unread
            const deletedNotif = notifications.find(notif => notif.id === notificationId);
            if (deletedNotif && !deletedNotif.read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    };

    // Handle notification click
    const handleNotificationClick = (notification: AppNotification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }

        // Debug: Log notification data to see what we have
        console.log('Notification clicked:', {
            id: notification.id,
            type: notification.type,
            jobCardId: notification.jobCardId,
            taskId: notification.taskId,
            data: notification.data
        });

        // Navigate based on notification type and available data
        const jobCardId = notification.jobCardId || (notification.data?.job_card_id as number);
        
        if (jobCardId) {
            // For job card assigned notifications, go to edit page (owner expected to work on it)
            if (notification.type === 'job_card_assigned') {
                navigate(`/jobcards/edit/${jobCardId}`);
            } else {
                // For other types, prefer edit unless explicitly a view-only type
                navigate(`/jobcards/edit/${jobCardId}`);
            }
        } else if (notification.taskId) {
            // For task-related notifications, we need the job card ID from the task
            navigate(`/jobcards/edit/${notification.taskId}`);
        } else {
            // If no specific navigation, just close the dropdown
            console.warn('No navigation target found for notification:', notification);
        }

        setIsOpen(false);
    };

    // Get notification icon
    const getNotificationIcon = (type: NotificationType) => {
        switch (type) {
            case 'job_card_assigned':
                return <AlertCircle className="w-4 h-4 text-blue-500" />;
            case 'job_card_reminder':
                return <Clock className="w-4 h-4 text-orange-500" />;
            case 'task_completed':
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'job_card_closed':
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'system_announcement':
                return <Info className="w-4 h-4 text-purple-500" />;
            default:
                return <Bell className="w-4 h-4 text-gray-500" />;
        }
    };

    // Format notification time
    const formatTime = (dateString: string) => {
        if (!dateString) return 'Unknown time';
        
        // Handle different date formats that might come from the backend
        let date: Date;
        
        // Try parsing as ISO string first
        if (typeof dateString === 'string' && dateString.includes('T')) {
            date = new Date(dateString);
        } else if (typeof dateString === 'string' && dateString.includes(' ')) {
            // Handle Laravel datetime format: "2024-01-15 10:30:00"
            date = new Date(dateString.replace(' ', 'T'));
        } else {
            date = new Date(dateString);
        }
        
        // Check if the date is valid
        if (isNaN(date.getTime())) {
            console.warn('Invalid date string received:', dateString, 'Type:', typeof dateString);
            return 'Invalid date';
        }
        
        const now = new Date();
        const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
        if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
        
        return date.toLocaleDateString();
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load notifications on mount
    useEffect(() => {
        if (user) {
            loadNotifications();
        }
    }, [user, loadNotifications]);

    // Poll for new notifications with adaptive frequency (15s visible, 120s hidden)
    useEffect(() => {
        if (!user) return;

        let interval: number | undefined;

        const getIntervalMs = () => (document.hidden ? 120000 : 15000);

        const schedule = () => {
            if (interval) window.clearInterval(interval);
            interval = window.setInterval(() => {
                if (!isOpen) {
                    loadNotifications(1, false);
                    // Note: keep requests modest; server pagination is 10 by default
                }
            }, getIntervalMs());
        };

        // Start polling and do an immediate refresh when becoming visible
        schedule();

        // Listen for visibility changes to reschedule and optionally refresh
        const handleVisibilityChange = () => {
            schedule();
            if (!document.hidden && !isOpen) {
                // Trigger an immediate refresh on focus for snappier UX
                loadNotifications(1, false, true);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (interval) window.clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user, loadNotifications, isOpen]);

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Bell Icon */}
            <button
                onClick={async () => {
                    try {
                        // If permission is still default, trigger native prompt
                        if (notificationService.isNotificationSupported()) {
                            const status = notificationService.getPermissionStatus();
                            if (status.default) {
                                await notificationService.requestPermission();
                            }
                        }
                    } catch (e) {
                        // no-op
                    } finally {
                        setIsOpen(!isOpen);
                    }
                }}
                className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                title="Notifications"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-[90vw] max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 transform -translate-x-1/2 left-1/2">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Notifications
                        </h3>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => loadNotifications(1, false, true)}
                                disabled={loading}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                                title="Refresh notifications"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                                onClick={async () => {
                                    try {
                                        await fetch('/api/admin/notifications/test', { method: 'POST', credentials: 'include' });
                                    } catch {}
                                }}
                                className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
                                title="Send test push (admin)"
                            >
                                Test push
                            </button>
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                    title="Mark all as read"
                                >
                                    Mark all read
                                </button>
                            )}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                                No notifications yet
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                                        !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                    }`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="flex items-start gap-3">
                                        {getNotificationIcon(notification.type)}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <h4 className={`text-sm font-medium ${
                                                    !notification.read 
                                                        ? 'text-gray-900 dark:text-white' 
                                                        : 'text-gray-700 dark:text-gray-300'
                                                }`}>
                                                    {notification.title}
                                                </h4>
                                                <div className="flex items-center gap-1">
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {formatTime(notification.createdAt)}
                                                    </span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            deleteNotification(notification.id);
                                                        }}
                                                        className="text-gray-400 hover:text-red-500 p-1"
                                                        title="Delete notification"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                            <p className={`text-sm mt-1 ${
                                                !notification.read 
                                                    ? 'text-gray-800 dark:text-gray-200' 
                                                    : 'text-gray-600 dark:text-gray-400'
                                            }`}>
                                                {notification.message}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}

                        {/* Load More Button */}
                        {hasMore && (
                            <div className="p-4 text-center">
                                <button
                                    onClick={loadMore}
                                    disabled={loading}
                                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50"
                                >
                                    {loading ? 'Loading...' : 'Load more'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => {
                                navigate('/notifications/settings');
                                setIsOpen(false);
                            }}
                            className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                            <Settings className="w-4 h-4" />
                            Notification Settings
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
